import { useState, useEffect } from 'react';

/**
 * Hook per recuperare dati meteo di un sentiero
 */
export function useTrailWeather(lat, lon, altitude, enabled = true) {
    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Validazione parametri
        if (!enabled) return;

        // ✅ IMPORTANTE: Gestisci diversi formati di input
        let finalLat, finalLon;

        // Se lat è un array (es. [lat, lon, alt] o [lon, lat, alt])
        if (Array.isArray(lat)) {
            console.warn('⚠️ lat è un array, estraggo valori:', lat);
            // Formato GeoJSON: [lon, lat, alt]
            finalLon = parseFloat(lat[0]);
            finalLat = parseFloat(lat[1]);
        }
        // Se lat è una stringa con virgole
        else if (typeof lat === 'string' && lat.includes(',')) {
            console.warn('⚠️ lat è stringa con virgole:', lat);
            const parts = lat.split(',').map(v => parseFloat(v.trim()));
            // Assumo formato [lon, lat] o [lat, lon]
            // Italia: lat ~40-47, lon ~7-18
            if (parts[0] > 30) {
                finalLat = parts[0];
                finalLon = parts[1];
            } else {
                finalLon = parts[0];
                finalLat = parts[1];
            }
        }
        // Valori normali
        else {
            finalLat = parseFloat(lat);
            finalLon = parseFloat(lon);
        }

        // Validazione finale
        if (isNaN(finalLat) || isNaN(finalLon)) {
            console.error('❌ Coordinate non valide:', { lat, lon, finalLat, finalLon });
            setError('Coordinate non valide');
            return;
        }

        // Verifica range Italia
        if (finalLat < 35 || finalLat > 48 || finalLon < 6 || finalLon > 19) {
            console.warn('⚠️ Coordinate fuori range Italia:', { finalLat, finalLon });
            // Potrebbe essere invertito lon/lat
            if (finalLon > 35 && finalLon < 48 && finalLat > 6 && finalLat < 19) {
                console.log('🔄 Inverto lat/lon');
                [finalLat, finalLon] = [finalLon, finalLat];
            }
        }

        console.log('✅ Coordinate finali:', {
            lat: finalLat,
            lon: finalLon,
            altitude: altitude
        });

        let isMounted = true;

        async function fetchWeather() {
            setLoading(true);
            setError(null);

            try {
                const backendUrl = process.env.REACT_APP_API_URL_NODE;

                // ✅ USA URLSearchParams per costruire URL correttamente
                const params = new URLSearchParams({
                    lat: finalLat.toString(),
                    lon: finalLon.toString(),
                    days: '7'
                });

                // ✅ AGGIUNGI altitude SOLO se è un numero valido
                if (altitude !== null && altitude !== undefined && !isNaN(altitude) && altitude > 0) {
                    params.append('altitude', Math.round(altitude).toString());
                    console.log(`📏 Altitude inclusa: ${Math.round(altitude)}m`);
                } else {
                    console.log('📏 Altitude non fornita - Open-Meteo la calcolerà automaticamente');
                }

                const url = `${backendUrl}/api/weather/trail?${params.toString()}`;

                console.log('🌐 Fetching weather:', url);

                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const result = await response.json();

                if (isMounted) {
                    setWeather(result.data);
                    setLoading(false);
                }

            } catch (err) {
                console.error('❌ Weather fetch error:', err);
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            }
        }

        fetchWeather();

        return () => {
            isMounted = false;
        };

    }, [lat, lon, altitude, enabled]);

    return { weather, loading, error };
}

/**
 * Hook leggero per solo dati neve
 */
export function useSnowData(lat, lon, altitude, enabled = true) {
    const [snow, setSnow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) return;

        // ✅ Stessa gestione coordinate
        let finalLat, finalLon;

        if (Array.isArray(lat)) {
            finalLon = parseFloat(lat[0]);
            finalLat = parseFloat(lat[1]);
        } else if (typeof lat === 'string' && lat.includes(',')) {
            const parts = lat.split(',').map(v => parseFloat(v.trim()));
            if (parts[0] > 30) {
                finalLat = parts[0];
                finalLon = parts[1];
            } else {
                finalLon = parts[0];
                finalLat = parts[1];
            }
        } else {
            finalLat = parseFloat(lat);
            finalLon = parseFloat(lon);
        }

        if (isNaN(finalLat) || isNaN(finalLon)) {
            console.error('❌ Coordinate non valide per snow');
            return;
        }

        let isMounted = true;

        async function fetchSnow() {
            setLoading(true);
            setError(null);

            try {
                const backendUrl = process.env.REACT_APP_API_URL_NODE;

                // ✅ USA URLSearchParams anche per snow
                const params = new URLSearchParams({
                    lat: finalLat.toString(),
                    lon: finalLon.toString()
                });

                // ✅ AGGIUNGI altitude SOLO se valido
                if (altitude !== null && altitude !== undefined && !isNaN(altitude) && altitude > 0) {
                    params.append('altitude', Math.round(altitude).toString());
                    console.log(`📏 Snow - altitude inclusa: ${Math.round(altitude)}m`);
                } else {
                    console.log('📏 Snow - altitude auto da Open-Meteo');
                }

                const url = `${backendUrl}/api/weather/snow?${params.toString()}`;

                console.log('🌐 Fetching snow data:', url);

                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const result = await response.json();

                if (isMounted) {
                    setSnow(result.data);
                    setLoading(false);
                }

            } catch (err) {
                console.error('❌ Snow fetch error:', err);
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            }
        }

        fetchSnow();

        return () => {
            isMounted = false;
        };

    }, [lat, lon, altitude, enabled]);

    return { snow, loading, error };
}